/**
 * src/registryConfig.js
 *
 * VISINVOKE Windows Knowledge Centre - central configuration.
 *
 * SINGLE SOURCE OF TRUTH for the Windows Knowledge Centre.
 *
 * The Windows Knowledge Centre is a growing collection of Windows knowledge,
 * tools, tweaks and technical resources. Registry Tweaks is one category within
 * it, not the product itself.
 *
 * To add a future category, add ONE entry to REGISTRY_CATEGORIES below.
 * No routing changes and no new page components are required: the dynamic
 * route /dashboard/utilities/registry/:categoryId renders every category
 * from this configuration.
 *
 * Each category carries its own accent colour. All visual identity (neon
 * border, soft glow, icon colour, hover brightening) is derived from this
 * accent so the section stays fully data-driven.
 *
 * This stage builds the framework only. No registry tweaks are defined here.
 */

// Base route prefix for every Knowledge Centre page.
export const REGISTRY_BASE_ROUTE = '/dashboard/utilities/registry';

// Status badge palette - brighter, modern. Used by status -> colour lookup.
export const REGISTRY_STATUS_COLORS = {
  'Available':   '#34d399',  // bright emerald
  'Coming Soon': '#fbbf24',  // amber
  'Planned':     '#60a5fa',  // blue
  'Development': '#a78bfa',  // purple
};

// Default per-category values while categories are unpopulated.
export const REGISTRY_DEFAULTS = {
  status: 'Coming Soon',
  tweaksAvailable: 0,
  lastUpdated: 'Not Yet Available',
};

// Resolve a status string to its badge colour.
export function getStatusColor(status) {
  return REGISTRY_STATUS_COLORS[status] || REGISTRY_STATUS_COLORS['Coming Soon'];
}

// Every category. id is used as the :categoryId route param.
// accent drives the neon border, glow, and icon colour for that category.
export const REGISTRY_CATEGORIES = [
  {
    id: 'login-security',
    icon: '\u{1F510}',
    title: 'Registry Tweaks',
    accent: '#34d399', // Emerald
    description: 'Carefully documented Windows registry tweaks covering sign-in behaviour, lock screen options, credential handling, and security-related settings, each with explanations, risk ratings, and full undo support.',
  },
  {
    id: 'desktop-explorer',
    icon: '\u{1F5A5}',
    title: 'Desktop & Explorer',
    accent: '#3b82f6', // Electric Blue
    description: 'File Explorer behaviour, desktop icons, folder views, thumbnails, and shell options that shape the everyday Windows desktop experience.',
  },
  {
    id: 'taskbar',
    icon: '\u{1F4CC}',
    title: 'Taskbar',
    accent: '#38bdf8', // Sky Blue
    description: 'Taskbar layout, system tray behaviour, button grouping, clock options, and notification area settings.',
  },
  {
    id: 'start-menu',
    icon: '\u{1F9E9}',
    title: 'Start Menu',
    accent: '#a3e635', // Lime
    description: 'Start Menu layout, recommended items, recently added apps, search integration, and tile or list behaviour.',
  },
  {
    id: 'context-menus',
    icon: '\u{1F5B1}',
    title: 'Context Menus',
    accent: '#fb923c', // Orange
    description: 'Right-click menu entries, classic versus modern menus, shell extensions, and menu ordering across files, folders, and the desktop.',
  },
  {
    id: 'performance',
    icon: '\u{26A1}',
    title: 'Performance',
    accent: '#fbbf24', // Amber
    description: 'Visual effects, animations, responsiveness, background service behaviour, and tuning options that influence overall system performance.',
  },
  {
    id: 'gaming',
    icon: '\u{1F3AE}',
    title: 'Gaming',
    accent: '#a855f7', // Purple
    description: 'Game Mode, Game Bar, GPU scheduling, fullscreen optimisations, and registry settings relevant to gaming performance and behaviour.',
  },
  {
    id: 'privacy',
    icon: '\u{1F575}',
    title: 'Privacy',
    accent: '#22d3ee', // Cyan
    description: 'Telemetry, advertising identifiers, activity history, app permissions, and data collection settings that affect Windows privacy.',
  },
  {
    id: 'windows-update',
    icon: '\u{1F501}',
    title: 'Windows Update',
    accent: '#f87171', // Red
    description: 'Update delivery, active hours, restart behaviour, driver updates, and policies that control how Windows Update operates.',
  },
  {
    id: 'networking',
    icon: '\u{1F310}',
    title: 'Networking',
    accent: '#2dd4bf', // Teal
    description: 'Network adapter behaviour, DNS handling, connection settings, sharing options, and registry settings related to connectivity.',
  },
  {
    id: 'storage-drives',
    icon: '\u{1F4BD}',
    title: 'Storage & Drives',
    accent: '#facc15', // Yellow
    description: 'Drive letters, AutoPlay, indexing, disk cleanup behaviour, and storage-related registry settings for local and removable drives.',
  },
  {
    id: 'onedrive',
    icon: '\u{2601}',
    title: 'OneDrive',
    accent: '#60a5fa', // Azure
    description: 'OneDrive integration, sync behaviour, Explorer presence, and settings that control how OneDrive appears and operates within Windows.',
  },
  {
    id: 'system-tools',
    icon: '\u{1F6E0}',
    title: 'System Tools',
    accent: '#cbd5e1', // Silver
    description: 'Built-in Windows tools, system dialogs, default behaviours, and registry settings tied to core operating system utilities.',
  },
  {
    id: 'developer-tools',
    icon: '\u{1F4BB}',
    title: 'Developer Tools',
    accent: '#818cf8', // Indigo
    description: 'Developer Mode, Windows features, terminal behaviour, file system options, and registry settings useful for development workflows.',
  },
  {
    id: 'recovery',
    icon: '\u{1F198}',
    title: 'Recovery',
    accent: '#f97316', // Deep Orange
    description: 'System Restore behaviour, recovery options, boot settings, and registry settings related to repairing and recovering Windows.',
  },
  {
    id: 'power-user',
    icon: '\u{1F9D1}',
    title: 'Power User',
    accent: '#eab308', // Gold
    description: 'Advanced tweaks, hidden options, workflow accelerators, and registry settings aimed at experienced Windows users.',
  },
  {
    id: 'accessibility',
    icon: '\u{267F}',
    title: 'Accessibility',
    accent: '#f472b6', // Pink
    description: 'Visual aids, input options, narrator and magnifier behaviour, and registry settings that support accessibility and ease of access.',
  },
  {
    id: 'miscellaneous',
    icon: '\u{1F4E6}',
    title: 'Miscellaneous',
    accent: '#f8fafc', // White
    description: 'Assorted registry tweaks and Windows resources that do not fit a single category, covering smaller customisations across the Windows experience.',
  },
];

// Fields every registry tweak will eventually provide. Used by the information
// panels. Informational only.
export const REGISTRY_TWEAK_FIELDS = [
  'Purpose',
  'Detailed explanation',
  'Registry path(s)',
  'Registry value(s)',
  'Registry value type',
  'Windows compatibility',
  'Administrator requirement',
  'Restart requirement',
  'Risk level',
  'Undo support',
  'Automatic backup support',
  'System Restore recommendation',
  'Notes',
  'Related tweaks',
  'Microsoft documentation links (where available)',
];

// Capability list shown on the landing page hero panel. Informational only.
export const REGISTRY_CAPABILITIES = [
  'Carefully documented Windows tweaks and resources',
  'Safe one-click application',
  'Automatic backup support',
  'Registry export support',
  'Windows compatibility information',
  'Administrator requirement indicators',
  'Restart requirement indicators',
  'Risk ratings',
  'Clear explanations',
  'Complete undo support',
];

// Helper: resolve a category by its route id.
export function getRegistryCategory(categoryId) {
  for (let i = 0; i < REGISTRY_CATEGORIES.length; i++) {
    if (REGISTRY_CATEGORIES[i].id === categoryId) return REGISTRY_CATEGORIES[i];
  }
  return null;
}

// Windows versions the Knowledge Centre targets. Informational placeholder.
export const WINDOWS_VERSIONS = ['Windows 11', 'Windows 10'];

// Content types the Knowledge Centre supports. The platform is NOT registry
// only: every category page is built around these three types so future
// stages can populate any of them.
export const KNOWLEDGE_CONTENT_TYPES = [
  { id: 'articles', label: 'Knowledge Articles', icon: '\u{1F4DA}' },
  { id: 'tweaks',   label: 'Registry Tweaks',    icon: '\u{1F527}' },
  { id: 'tools',    label: 'Windows Tools',      icon: '\u{1F6E0}' },
];

// Summary stat definitions shown beneath the hero on a category page.
// Values are dynamic placeholders (all 0 until content is populated).
export const CATEGORY_STAT_DEFS = [
  { id: 'articles', label: 'Total Articles',   value: 0,  suffix: '' },
  { id: 'tweaks',   label: 'Registry Tweaks',  value: 0,  suffix: '' },
  { id: 'guides',   label: 'Knowledge Guides', value: 0,  suffix: '' },
  { id: 'versions', label: 'Windows Versions', value: WINDOWS_VERSIONS.length, suffix: ' Supported' },
];

// Difficulty levels for the library filter. Placeholder values only.
export const DIFFICULTY_LEVELS = ['All Levels', 'Beginner', 'Intermediate', 'Advanced'];

// Sort options for the library. Placeholder values only.
export const LIBRARY_SORT_OPTIONS = ['Newest', 'Most Popular', 'A to Z', 'Difficulty'];

// Default FAQ questions for every category. Designed to become data-driven:
// a category may later override this with its own faq array.
export const REGISTRY_FAQ = [
  {
    q: 'What does this category cover?',
    a: 'This category groups related Windows knowledge, guides and tweaks under a single focus area. As the library grows, every entry here will share a common theme so you can find what you need quickly.',
  },
  {
    q: 'Do these tweaks work on Windows 11?',
    a: 'Every entry will list its supported Windows versions. The Knowledge Centre targets Windows 11 and Windows 10, and each entry will clearly state its compatibility before you apply anything.',
  },
  {
    q: 'Are changes reversible?',
    a: 'Yes. Every documented change is designed with complete undo support, and automatic backups are taken before changes are applied wherever possible.',
  },
  {
    q: 'Will administrator rights be required?',
    a: 'Some changes require administrator privileges. Each entry includes a clear administrator requirement indicator so you know before you begin.',
  },
  {
    q: 'How do I undo changes?',
    a: 'Each entry will document its exact undo steps, and where automatic backups are taken you will be able to restore the previous state. System Restore is also recommended as an extra safety net.',
  },
  {
    q: 'Can I create backups?',
    a: 'Yes. The Knowledge Centre encourages backups before any change, supports registry export, and recommends creating a System Restore point for additional protection.',
  },
];

// Safety guidance shown in the Safety Centre. Informational only.
export const REGISTRY_SAFETY = [
  'Always create a backup before applying any change.',
  'Understand every change fully before you apply it.',
  'Administrator rights may be required for some changes.',
  'Some changes require a restart or sign out to take effect.',
  'Creating a System Restore point is strongly recommended.',
  'Registry modifications should only be applied when understood.',
];

// Helper: dynamically derive related categories for a given category id.
// No hardcoded relationships - related categories are simply the other
// categories that follow this one in the configuration (wrapping around),
// so the set updates automatically as categories are added or reordered.
export function getRelatedCategories(categoryId, count) {
  const n = count || 4;
  const idx = REGISTRY_CATEGORIES.findIndex(c => c.id === categoryId);
  if (idx === -1) return REGISTRY_CATEGORIES.slice(0, n);
  const out = [];
  for (let step = 1; step <= REGISTRY_CATEGORIES.length - 1 && out.length < n; step++) {
    out.push(REGISTRY_CATEGORIES[(idx + step) % REGISTRY_CATEGORIES.length]);
  }
  return out;
}
